/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { of, throwError } from "rxjs";
import { HttpRequestOptions } from "../../../../models/ui/http-request-options.model";
import { HttpParams } from "@angular/common/http";
import { ApplicationError } from "../../../../models/ui/application-error.model";
import { isNullOrUndefined } from "../../../utils";
import {
	SERVICE_NOW_CATEGORIES_RESPONSE,
	SERVICE_NOW_DEVICE_CARBON_FOOTPRINT_RESPONSE,
	SERVICE_NOW_DEVICE_STATUS_PIE_RESPONSE,
	SERVICE_NOW_GENERAL_INFORMATION_RESPONSE,
	SERVICE_NOW_LIFECYCLE_SAVINGS_OVER_TIME_RESPONSE,
	SERVICE_NOW_LOCATION_OVER_TIME_RESPONSE,
	SERVICE_NOW_LOCATIONS_BAR_PER_DEVICE_RESPONSE,
	SERVICE_NOW_LOCATIONS_BAR_RESPONSE,
	SERVICE_NOW_MANUFACTURER_EMISSION_PIE_RESPONSE,
	SERVICE_NOW_MANUFACTURER_TABLE_RESPONSE,
	SERVICE_NOW_MANUFACTURERS_RESPONSE,
	SERVICE_NOW_MODELS_BAR_PER_DEVICE_RESPONSE,
	SERVICE_NOW_MODELS_BAR_RESPONSE,
	SERVICE_NOW_SCOPE_CARBON_FOOTPRINT_RESPONSE,
	SERVICE_NOW_TOTAL_OVER_TIME_RESPONSE
} from "./service-now.db";

export class ServiceNow {
	private static _getHttpParams(options: HttpRequestOptions, methodName: string) {
		const httpParams = options.params as HttpParams;

		if (!httpParams)
			throw new ApplicationError("No params provided", ServiceNow.name, methodName);

		const startDate = httpParams.get("startDate");

		if (!startDate)
			throw new ApplicationError("Missing required parameters", ServiceNow.name, methodName);

		return httpParams;
	}

	public static getDevicesCategories(options: HttpRequestOptions) {
		ServiceNow._getHttpParams(options, ServiceNow.getDevicesCategories.name);

		return of(SERVICE_NOW_CATEGORIES_RESPONSE);
	}

	public static getGeneralInformation(options: HttpRequestOptions) {
		ServiceNow._getHttpParams(options, ServiceNow.getGeneralInformation.name);

		return of(SERVICE_NOW_GENERAL_INFORMATION_RESPONSE);
	}

	public static getDeviceCarbonFootprint(options: HttpRequestOptions) {
		ServiceNow._getHttpParams(options, ServiceNow.getDeviceCarbonFootprint.name);

		return of(SERVICE_NOW_DEVICE_CARBON_FOOTPRINT_RESPONSE);
	}

	public static postScopeCarbonFootprintBar(options: HttpRequestOptions) {
		ServiceNow._getHttpParams(options, ServiceNow.postScopeCarbonFootprintBar.name);

		if (isNullOrUndefined(options.body))
			return throwError(
				() =>
					new ApplicationError(
						"No body provided",
						ServiceNow.name,
						ServiceNow.postScopeCarbonFootprintBar.name
					)
			);

		return of(SERVICE_NOW_SCOPE_CARBON_FOOTPRINT_RESPONSE);
	}

	public static getScopeCarbonFootprintTable(options: HttpRequestOptions) {
		ServiceNow._getHttpParams(options, ServiceNow.getScopeCarbonFootprintTable.name);

		return of(SERVICE_NOW_MANUFACTURERS_RESPONSE);
	}

	public static postManufacturerEmissionPie(options: HttpRequestOptions) {
		ServiceNow._getHttpParams(options, ServiceNow.postManufacturerEmissionPie.name);

		if (isNullOrUndefined(options.body))
			return throwError(
				() =>
					new ApplicationError(
						"No body provided",
						ServiceNow.name,
						ServiceNow.postManufacturerEmissionPie.name
					)
			);

		return of(SERVICE_NOW_MANUFACTURER_EMISSION_PIE_RESPONSE);
	}

	public static getDeviceStatusEmissionPie(options: HttpRequestOptions) {
		ServiceNow._getHttpParams(options, ServiceNow.getDeviceStatusEmissionPie.name);

		return of(SERVICE_NOW_DEVICE_STATUS_PIE_RESPONSE);
	}

	public static getDevicesModelsBar(options: HttpRequestOptions) {
		ServiceNow._getHttpParams(options, ServiceNow.getDevicesModelsBar.name);

		return of(SERVICE_NOW_MODELS_BAR_RESPONSE);
	}

	public static getDevicesModelsBarPerDevice(options: HttpRequestOptions) {
		ServiceNow._getHttpParams(options, ServiceNow.getDevicesModelsBarPerDevice.name);

		return of(SERVICE_NOW_MODELS_BAR_PER_DEVICE_RESPONSE);
	}

	public static getDevicesLocationBar(options: HttpRequestOptions) {
		ServiceNow._getHttpParams(options, ServiceNow.getDevicesLocationBar.name);

		return of(SERVICE_NOW_LOCATIONS_BAR_RESPONSE);
	}

	public static getDevicesLocationBarPerDevice(options: HttpRequestOptions) {
		ServiceNow._getHttpParams(options, ServiceNow.getDevicesLocationBarPerDevice.name);

		return of(SERVICE_NOW_LOCATIONS_BAR_PER_DEVICE_RESPONSE);
	}

	public static getTotalDevicesOverTime(options: HttpRequestOptions) {
		ServiceNow._getHttpParams(options, ServiceNow.getTotalDevicesOverTime.name);

		return of(SERVICE_NOW_TOTAL_OVER_TIME_RESPONSE);
	}

	public static getLocationDevicesOverTime(options: HttpRequestOptions) {
		ServiceNow._getHttpParams(options, ServiceNow.getLocationDevicesOverTime.name);

		return of(SERVICE_NOW_LOCATION_OVER_TIME_RESPONSE);
	}

	public static getLifecycleSavingsDevicesOverTime(options: HttpRequestOptions) {
		ServiceNow._getHttpParams(options, ServiceNow.getLifecycleSavingsDevicesOverTime.name);

		return of(SERVICE_NOW_LIFECYCLE_SAVINGS_OVER_TIME_RESPONSE);
	}

	public static getManufacturerEmissionTable(options: HttpRequestOptions) {
		ServiceNow._getHttpParams(options, ServiceNow.getManufacturerEmissionTable.name);

		if (isNullOrUndefined(options.body))
			return throwError(
				() =>
					new ApplicationError(
						"No body provided",
						ServiceNow.name,
						ServiceNow.getManufacturerEmissionTable.name
					)
			);

		return of(SERVICE_NOW_MANUFACTURER_TABLE_RESPONSE);
	}
}
